import type { Mixpanel } from 'mixpanel-browser';
import type {
  AnalyticsEventArgs,
  AnalyticsEventName,
  AnalyticsEvents,
} from './events';

/**
 * The one place the apps talk to Mixpanel. Everything else calls these
 * functions, so the vendor can change without touching a call site, and code
 * that tracks never has to check whether tracking is on.
 *
 * Like the API layer, this lib reads no `import.meta.env`: each app calls
 * `configureAnalytics` from its `src/analyticsRuntime.ts` at startup.
 */

export interface AnalyticsConfig {
  /**
   * Mixpanel project token. Left empty, or still a `___VITE_…___` placeholder
   * because the environment's secret has no such key, tracking stays off and
   * every call below does nothing.
   */
  token?: string;
  /**
   * Ingestion host. Defaults to `DEFAULT_API_HOST` (EU) when missing or still a
   * placeholder: the project is EU-resident, and events sent to the SDK's own
   * US default are accepted with a 200 and then silently dropped.
   */
  apiHost?: string;
  /** Sent with every event (`dev`, `qa`, `production`, …) to split one project by environment. */
  environment?: string;
  /** Which app sent the event. */
  app: 'main' | 'super-admin';
  /** Sent with every event as `app_version`. */
  version?: string;
  /** Logs every call to the console. */
  debug?: boolean;
}

/** Values Mixpanel accepts as event, super or profile properties. */
export type AnalyticsValue = string | number | boolean | null | undefined;
export type AnalyticsProperties = Record<string, AnalyticsValue>;

export interface AnalyticsUser {
  /** Stable id for the person: the Cognito `sub`. */
  id: string;
  email?: string;
  name?: string;
  /**
   * Stored on the user's profile and sent with every later event, so reports
   * can be broken down by them — role, organisation, department.
   */
  properties?: AnalyticsProperties;
}

export interface TrackOptions {
  /**
   * Send now with `navigator.sendBeacon`, which survives the page unloading.
   * For events tracked just before a redirect, like signing out.
   */
  beacon?: boolean;
}

type Call = (mixpanel: Mixpanel) => void;

export const DEFAULT_API_HOST = 'https://api-eu.mixpanel.com';

/**
 * idle: not configured (tests, or an app that never calls configure).
 * loading: configured, SDK still downloading; calls wait in `pending`.
 */
let state: 'idle' | 'disabled' | 'loading' | 'ready' = 'idle';
let client: Mixpanel | null = null;
let pending: Call[] = [];
/** Also stored on the user's profile, so People reports can filter by it. */
let environment = 'unknown';

/** Undefined for a missing value or an ECS placeholder nobody replaced. */
const configValue = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && !/^___\w+___$/.test(trimmed) ? trimmed : undefined;
};

const run = (call: Call): void => {
  if (state === 'ready' && client) {
    call(client);
  } else if (state === 'loading') {
    pending.push(call);
  }
};

export const configureAnalytics = (config: AnalyticsConfig): void => {
  if (state !== 'idle') return;

  const token = configValue(config.token);
  if (!token) {
    state = 'disabled';
    return;
  }

  state = 'loading';
  environment = configValue(config.environment) ?? 'unknown';
  // Loaded on demand so the SDK stays out of the entry chunk.
  import('mixpanel-browser')
    .then(({ default: mixpanel }) => {
      mixpanel.init(token, {
        api_host: configValue(config.apiHost) ?? DEFAULT_API_HOST,
        debug: !!config.debug,
        persistence: 'localStorage',
        // Clicks, form submits, rage and dead clicks, and a page view whenever
        // the path changes. Never element text or typed input: file and
        // contract names must not leave the app.
        autocapture: {
          pageview: 'url-with-path',
          click: true,
          input: true,
          submit: true,
          scroll: false,
          capture_text_content: false,
        },
      });
      mixpanel.register({
        app: config.app,
        environment,
        app_version: config.version,
      });

      client = mixpanel;
      state = 'ready';
      const queued = pending;
      pending = [];
      queued.forEach((call) => call(mixpanel));
    })
    .catch((error) => {
      console.error('Analytics failed to load; tracking is off:', error);
      state = 'disabled';
      pending = [];
    });
};

// The overload checks callers against the event's own properties; the
// implementation signature below it is what the generic tuple flattens to.
export function track<E extends AnalyticsEventName>(
  event: E,
  ...args: [...AnalyticsEventArgs<E>, TrackOptions?]
): void;
export function track(
  event: AnalyticsEventName,
  properties?: AnalyticsEvents[AnalyticsEventName],
  options?: TrackOptions
): void {
  run((mixpanel) =>
    mixpanel.track(
      event,
      properties,
      options?.beacon
        ? { transport: 'sendBeacon', send_immediately: true }
        : undefined
    )
  );
}

/** Ties this browser's events to the user and keeps their profile up to date. */
export const identify = ({ id, email, name, properties }: AnalyticsUser) =>
  run((mixpanel) => {
    mixpanel.identify(id);
    // Each environment has its own Cognito pool, so its own `sub` and its own
    // profile: one `environment` value per profile never gets overwritten.
    mixpanel.people.set({
      $email: email,
      $name: name,
      environment,
      ...properties,
    });
    if (properties) mixpanel.register(properties);
  });

/** Profile properties, overwritten each time. */
export const setUserProperties = (properties: AnalyticsProperties) =>
  run((mixpanel) => mixpanel.people.set(properties));

/** Profile properties written once and never overwritten, like a first login. */
export const setUserPropertiesOnce = (properties: AnalyticsProperties) =>
  run((mixpanel) => mixpanel.people.set_once(properties));

/** Adds to a numeric profile property, like a login count. */
export const incrementUserProperty = (property: string, by = 1) =>
  run((mixpanel) => mixpanel.people.increment(property, by));

/** Starts a timer; the next `track` of this event carries `$duration`. */
export const timeEvent = (event: AnalyticsEventName) =>
  run((mixpanel) => mixpanel.time_event(event));

/**
 * Forgets the user in this browser, so whoever signs in next on it is not
 * merged into them. Call after the sign-out event, never before.
 */
export const resetAnalytics = () => run((mixpanel) => mixpanel.reset());
